import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";

function Viewstock() {
  const [responseData, setResponseData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [deleteShowModals, setDeleteShowModals] = useState(false);
  const [getdeleteid, setdeleteid] = useState("");
  const [getdeletename, setdeletename] = useState("");

  const toast = useToast();
  const navigate = useNavigate();
  const { stock_ids } = useParams();

  useEffect(() => {
    if (stock_ids) {
      fetchSingleItem(stock_ids);
    } else {
      fetchData();
    }
  }, [stock_ids]);

  // Reset current page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${ApiUrl}/Api/stock.php`);
      console.log("All stock data:", response.data);
      
      // Handle different response structures
      let stockData = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        stockData = response.data.data;
      } else if (Array.isArray(response.data)) {
        stockData = response.data;
      } else if (response.data?.items && Array.isArray(response.data.items)) {
        stockData = response.data.items;
      }
      
      console.log("Processed stock data:", stockData);
      setResponseData(stockData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data", 3000, "topRight");
      setResponseData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSingleItem = async (itemId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${ApiUrl}/Api/stock.php/${itemId}`);
      console.log("Single item data:", response.data);
      
      // Parse the stock IDs from URL parameter
      const requestedStockIds = itemId.split(',').map(id => parseInt(id.trim()));
      console.log("Requested stock IDs:", requestedStockIds);
      
      let items = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      } else if (Array.isArray(response.data)) {
        items = response.data;
      }
      
      console.log("All items from API:", items);
      
      // Find the item that contains the requested stock IDs
      let matchingItem = null;
      for (const item of items) {
        if (item.stock_ids && Array.isArray(item.stock_ids)) {
          // Check if any of the requested stock IDs exist in this item's stock_ids
          const hasMatchingStockIds = requestedStockIds.some(requestedId => 
            item.stock_ids.includes(requestedId)
          );
          
          if (hasMatchingStockIds) {
            matchingItem = item;
            break;
          }
        }
      }
      
      console.log("Matching item found:", matchingItem);
      
      if (matchingItem && matchingItem.stock_list && Array.isArray(matchingItem.stock_list)) {
        // Filter stock_list to show only requested stock IDs
        const filteredStockList = matchingItem.stock_list.filter(stock => 
          requestedStockIds.includes(parseInt(stock.overall_stock_id))
        );
        
        console.log("Filtered stock list:", filteredStockList);
        
        // Convert each stock item to display format
        const formattedData = filteredStockList.map(stock => ({
          id: stock.id,
          item: stock.item || matchingItem.item,
          category: stock.category || matchingItem.category,
          weight: stock.weight || matchingItem.weight,
          date: stock.date,
          quantity: stock.quantity,
          sold_quantity: stock.sold_quantity,
          available_quantity: stock.available_quantity,
          overall_stock_id: stock.overall_stock_id,
          status: stock.status,
          // Keep reference to parent item data
          parent_item: matchingItem.item,
          parent_category: matchingItem.category,
          parent_weight: matchingItem.weight
        }));
        
        console.log("Final formatted data:", formattedData);
        setResponseData(formattedData);
      } else {
        console.log("No matching item or stock_list found");
        setResponseData([]);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      toast.error("Failed to fetch item", 3000, "topRight");
      setResponseData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (item) => {
    // Navigate to edit with the stock_ids from the item
    if (stock_ids) {
      // If we're viewing specific stock IDs, edit that specific stock
      navigate(`/edit-stock/${item.overall_stock_id}`);
    } else if (item.stock_ids && item.stock_ids.length > 0) {
      // If we're in summary view, edit all stock IDs for this item
      navigate(`/edit-stock/${item.stock_ids.join(',')}`);
    } else {
      navigate(`/edit-stock/${item.id}`);
    }
  };

  const handleViewDetails = (item) => {
    // Navigate to view specific stock_ids
    if (item.stock_ids && item.stock_ids.length > 0) {
      navigate(`/Viewstock/${item.stock_ids.join(',')}`);
    }
  };

  const handleOpenModal = (id, item) => {
    setdeleteid(id);
    setdeletename(item);
    setDeleteShowModals(true);
  };

  const handleCloseModal = () => {
    setDeleteShowModals(false);
  };

  // Updated handleDelete function to match PHP backend expectations
  const handleDelete = async () => {
    try {
      let deleteData;
      
      if (stock_ids) {
        // If we're viewing specific stock IDs, delete the specific stock item
        deleteData = {
          stock_ids: [parseInt(getdeleteid)]
        };
      } else {
        // If we're in summary view, we might want to delete all related stock items
        // or just the main stock entry - adjust this based on your requirements
        deleteData = {
          stock_ids: [parseInt(getdeleteid)]
        };
      }

      const response = await axios.delete(
        `${ApiUrl}/Api/stock.php`,
        {
          data: deleteData,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log("Deleted item:", response);
      setDeleteShowModals(false);
      toast.success("Deleted successfully!", 3000, "topRight");
      
      // Refresh the data or navigate back
      if (stock_ids) {
        navigate("/stocks");
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      
      // Handle different error responses
      let errorMessage = "Failed to delete item";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, 3000, "topRight");
    }
  };

  // Filter data based on search term
  const filteredData = responseData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.item && item.item.toLowerCase().includes(searchLower)) ||
      (item.category && item.category.toLowerCase().includes(searchLower)) ||
      (item.quantity && item.quantity.toString().includes(searchLower)) ||
      (item.overall_stock_id && item.overall_stock_id.toString().includes(searchLower)) ||
      (item.weight && item.weight.toString().includes(searchLower))
    );
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);

  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  // Format weight display
  const formatWeight = (weight) => {
    if (!weight) return 'N/A';
    return weight.toString().includes('g') ? weight : `${weight}g`;
  };

  // Format date display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return date;
    }
  };

  return (
    <div>
      <div className="card shadow-sm mt-5">
        <div className="card-body">
          {/* Header section */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="card-title mb-0">
              {stock_ids ? `Daywise Stock Details ` : 'All Stock Items'}
            </h4>
            {stock_ids && (
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/stocks')}
              >
                <i className="fa-solid fa-arrow-left me-2"></i>
                Back to All Items
              </button>
            )}
          </div>

          {/* Controls section - only show when not viewing specific stock IDs */}
          {!stock_ids && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <label className="form-label text-dark mb-0 me-2">Show: </label>
                <select
                  className="form-control"
                  style={{ width: 'auto' }}
                  onChange={handleItemsPerPageChange}
                  value={showAllItems ? "All" : itemsPerPage}
                >
                  <option value="All">All</option>
                  {[5, 10, 20, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
              </div>
              <div className="search-container">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search items, category, quantity..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: '250px' }}
                />
              </div>
            </div>
          )}

          {/* Table section */}
          <div className="card tableCard p-2 mt-3">
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading stock data...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover w-100">
                  <thead className="table-light">
                    <tr>
                      <th>S.No</th>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Item</th>
                      <th>Weight</th>
                      <th>Date</th>
                      <th>Quantity</th>
                      {/* <th>Sold Qty</th>
                      <th>Available Qty</th> */}
                      {/* <th>Total quantity</th> */}
                    
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => {
                        const serialNumber = showAllItems 
                          ? index + 1 
                          : (currentPage - 1) * itemsPerPage + index + 1;
                        
                        return (
                          <tr key={item.id || `item-${index}`}>
                            <td>{serialNumber}</td>
                            {stock_ids ? (
                              // When viewing specific stock IDs, show individual stock data
                              <>
                                {/* <td>
                                  <span className="badge bg-primary">
                                    {item.overall_stock_id}
                                  </span>
                                </td> */}
                                <td>{item.date}</td>
                                <td>{item.category}</td>
                                <td>{item.item}</td>
                                <td>{formatWeight(item.weight)}</td>
                                <td>{formatDate(item.date)}</td>
                                <td>
                                  <span className="badge bg-info text-dark">
                                    {item.quantity}
                                  </span>
                                </td>
                                {/* <td>
                                  <span className="badge bg-warning text-dark">
                                    {item.sold_quantity || 0}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-success">
                                    {item.available_quantity || 0}
                                  </span>
                                </td> */}
                              </>
                            ) : (
                              // When viewing all items, show summary data
                              <>
                                <td>
                                  {item.stock_ids && item.stock_ids.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-1">
                                      {item.stock_ids.map(stockId => (
                                        <span key={stockId} className="badge bg-primary">
                                          {stockId}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>{item.category}</td>
                                <td>{item.item}</td>
                                <td>{formatWeight(item.weight)}</td>
                                <td>
                                  {item.stock_list && item.stock_list.length > 0 
                                    ? formatDate(item.stock_list[0].date) 
                                    : 'N/A'}
                                </td>
                                <td>
                                  <span className="badge bg-info text-dark">
                                    {item.total_quantity || 0}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-warning text-dark">
                                    {item.sold_quantity || 0}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-success">
                                    {item.avaliable_quantity || item.available_quantity || 0}
                                  </span>
                                </td>
                              </>
                            )}
                           
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa-solid fa-box-open fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No stock data available</h5>
                            <p className="text-muted">
                              {searchTerm 
                                ? `No items found matching "${searchTerm}"` 
                                : "No stock items found in the system"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination - only show when not viewing specific stock IDs and not showing all items */}
            {!stock_ids && !showAllItems && totalPages > 1 && (
              <nav aria-label="Page navigation" className="mt-3">
                <ul className="pagination justify-content-end">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {renderPageNumbers()}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            {/* Summary info */}
            {!stock_ids && totalItems > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 text-muted small">
                <span>
                  Showing {currentItems.length} of {totalItems} items
                  {searchTerm && ` (filtered from ${responseData.length} total)`}
                </span>
                {!showAllItems && (
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteShowModals && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa-solid fa-exclamation-triangle text-warning me-2"></i>
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Are you sure you want to delete <strong>"{getdeletename}"</strong>?
                </p>
                <div className="alert alert-warning">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  This action cannot be undone and will permanently remove this item from the system.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  <i className="fa-solid fa-trash me-2"></i>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Viewstock;